'use client'

import { ButtonPrimary } from 'fukku/Button/Primary'
import { ButtonSecondary } from 'fukku/Button/Secondary'
import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useEffect, useRef, useState } from 'react'
import { SAVE_DATA_STATUS } from 'refunds/constants/constants'
import { useSaveRefundData } from 'refunds/hooks/useSaveRefundData/useSaveRefundData'
import type { RefundData } from 'refunds/types/refundData'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'

import style from './SaveErrorModal.module.scss'
import text from 'fukku/styles/texts.module.scss'

interface SaveErrorModalProps {
	readonly refundData: RefundData
	readonly handleRequestStatus: (status: string) => void
}

export function SaveErrorModal({
	refundData,
	handleRequestStatus,
}: SaveErrorModalProps) {
	const { t } = useLabels()
	const [isLoading, setIsLoading] = useState(false)
	const { isSmall } = useResponsive()
	const {
		country: { countryISO },
	} = useMasterData()
	const modalRef = useRef<ModalAPI>(null)

	const { saveRefundData } = useSaveRefundData({ countryISO })

	const handleOnModalClose = () => {
		handleRequestStatus('')
	}

	const handleRetry = async () => {
		setIsLoading(true)

		const { isValid } = await saveRefundData(refundData)

		setIsLoading(false)
		if (isValid) {
			modalRef.current?.close() // TODO Remove when Snackbar works properly
			handleRequestStatus(SAVE_DATA_STATUS.OK)
		}
	}

	useEffect(() => {
		modalRef.current?.show()
	}, [modalRef.current])

	const actions = [
		isSmall && (
			<ButtonSecondary
				key='cancel'
				onClick={handleOnModalClose}
				className={style.button}
			>
				{t('myRefunds.modal.errorSaving.cancel.button')}
			</ButtonSecondary>
		),
		<ButtonPrimary
			key='confirm'
			onClick={handleRetry}
			isLoading={isLoading}
			className={style.button}
		>
			{t('myRefunds.modal.errorSaving.retry.button')}
		</ButtonPrimary>,
	]

	return (
		<Modal
			name='refunds-save-error-modal'
			title={t('myRefunds.modal.errorSaving.title')}
			ref={modalRef}
			onClose={handleOnModalClose}
			type={MODAL_TYPE.SHEET_SMALL}
			data-testid='refunds.modal.errorOnSave'
			className={style.body}
		>
			<div className={style.content}>
				<h1 className={`${text.titleL} ${style.title}`}>
					{t('myRefunds.modal.errorSaving.title')}
				</h1>
				<p className={`${text.bodyM} ${style.text}`}>
					{t('myRefunds.modal.errorSaving.bodycopy')}
				</p>
				{actions.length > 0 && (
					<div className={style.actions}>{actions.map((a) => a)}</div>
				)}
			</div>
		</Modal>
	)
}
